// Learning Commons Knowledge Graph — mock data for prompt calibration
// Replace fetchLCKG with a real API call when available:
//   GET /api/lckg/standard/:code (proxy on ardent-api)

export interface LCKGLearningComponent {
  id: string
  description: string
}

export interface LCKGConnection {
  hop: 1 | 2 | 3
  type: 'buildsTowards' | 'relatesTo'
  direction: 'forward' | 'backward'
  standard: { code: string; description: string; gradeLevel: string[] }
  lcs: LCKGLearningComponent[]
}

export interface LCKGData {
  standard: { code: string; description: string; gradeLevel: string[] }
  learningComponents: LCKGLearningComponent[]
  connections: LCKGConnection[]
}

const MOCK_LCKG: Record<string, LCKGData> = {
  '4.OA.4': {
    standard: {
      code: '4.OA.4',
      description:
        'Find all factor pairs for a whole number in the range 1–100. Recognize that a whole number is a multiple of each of its factors. Determine whether a given whole number in the range 1–100 is prime or composite.',
      gradeLevel: ['4'],
    },
    learningComponents: [
      { id: 'lc-001', description: 'Identify factor pairs of whole numbers within 100' },
      { id: 'lc-002', description: 'Recognize multiples of a given number within 100' },
      { id: 'lc-003', description: 'Determine whether a whole number is prime or composite' },
      { id: 'lc-004', description: 'Use multiplication to find all factors of a number' },
    ],
    connections: [
      {
        hop: 1,
        type: 'buildsTowards',
        direction: 'forward',
        standard: {
          code: '5.OA.3',
          description: 'Generate two numerical patterns using two given rules.',
          gradeLevel: ['5'],
        },
        lcs: [
          { id: 'lc-101', description: 'Identify and extend numerical patterns using rules' },
          { id: 'lc-102', description: 'Relate corresponding terms in two patterns' },
        ],
      },
      {
        hop: 1,
        type: 'relatesTo',
        direction: 'forward',
        standard: {
          code: '4.OA.1',
          description: 'Interpret a multiplication equation as a comparison.',
          gradeLevel: ['4'],
        },
        lcs: [
          { id: 'lc-103', description: 'Interpret multiplication as comparison (e.g. 35 = 5 × 7)' },
          { id: 'lc-104', description: 'Write multiplication equations to represent comparison statements' },
        ],
      },
      {
        hop: 1,
        type: 'buildsTowards',
        direction: 'backward',
        standard: { code: '3.OA.7', description: 'Fluently multiply and divide within 100.', gradeLevel: ['3'] },
        lcs: [
          { id: 'lc-105', description: 'Recall multiplication facts within 100' },
          { id: 'lc-106', description: 'Use strategies to multiply within 100' },
        ],
      },
      {
        hop: 2,
        type: 'buildsTowards',
        direction: 'forward',
        standard: {
          code: '6.NS.4',
          description: 'Find the GCF of two whole numbers less than or equal to 100.',
          gradeLevel: ['6'],
        },
        lcs: [
          { id: 'lc-201', description: 'Find the greatest common factor of two numbers' },
          { id: 'lc-202', description: 'Find the least common multiple of two numbers' },
        ],
      },
      {
        hop: 2,
        type: 'relatesTo',
        direction: 'forward',
        standard: {
          code: '4.NBT.1',
          description:
            'Recognize that in a multi-digit number, a digit in one place represents ten times what it represents in the place to its right.',
          gradeLevel: ['4'],
        },
        lcs: [{ id: 'lc-203', description: 'Understand place value relationships in multi-digit numbers' }],
      },
      {
        hop: 3,
        type: 'buildsTowards',
        direction: 'forward',
        standard: {
          code: '7.NS.1',
          description: 'Apply properties of operations to add and subtract rational numbers.',
          gradeLevel: ['7'],
        },
        lcs: [{ id: 'lc-301', description: 'Add and subtract integers using properties of operations' }],
      },
    ],
  },
  '4.NF.1': {
    standard: {
      code: '4.NF.1',
      description: 'Explain why a fraction a/b is equivalent to (n×a)/(n×b) using visual fraction models.',
      gradeLevel: ['4'],
    },
    learningComponents: [
      { id: 'lc-f001', description: 'Explain fraction equivalence using visual models' },
      { id: 'lc-f002', description: 'Generate equivalent fractions by multiplying numerator and denominator' },
      { id: 'lc-f003', description: 'Identify equivalent fractions on a number line' },
    ],
    connections: [
      {
        hop: 1,
        type: 'buildsTowards',
        direction: 'forward',
        standard: {
          code: '4.NF.3',
          description: 'Understand a fraction a/b with a > 1 as a sum of fractions 1/b.',
          gradeLevel: ['4'],
        },
        lcs: [{ id: 'lc-f101', description: 'Decompose a fraction into a sum of unit fractions' }],
      },
      {
        hop: 1,
        type: 'buildsTowards',
        direction: 'backward',
        standard: {
          code: '3.NF.1',
          description:
            'Understand a fraction 1/b as the quantity formed by 1 part when a whole is partitioned into b equal parts.',
          gradeLevel: ['3'],
        },
        lcs: [
          {
            id: 'lc-f201',
            description: 'Understand unit fractions as one part of a whole partitioned into equal parts',
          },
        ],
      },
      {
        hop: 2,
        type: 'buildsTowards',
        direction: 'forward',
        standard: {
          code: '5.NF.1',
          description: 'Add and subtract fractions with unlike denominators.',
          gradeLevel: ['5'],
        },
        lcs: [
          { id: 'lc-f301', description: 'Find common denominators to add fractions' },
          { id: 'lc-f302', description: 'Use equivalent fractions to perform addition' },
        ],
      },
    ],
  },
}

export async function fetchLCKG(stdCode: string): Promise<LCKGData | null> {
  // Simulate network latency
  await new Promise((r) => setTimeout(r, 600))
  // TODO: replace with real API call:
  // return fetch(`/api/lckg/standard/${stdCode}`).then(r => r.json())
  return MOCK_LCKG[stdCode] ?? null
}
